/*
 * Copyright (c) 2023 Roboto Technologies, Inc.
 */
import {
  Avatar,
  Box,
  List,
  ListItem,
  ListItemText,
  useTheme,
} from "@mui/material";
import * as React from "react";

import { useAuth } from "@/providers";
import { UserRecord } from "@/shared/domain/users";

import { EditableProfileField } from "./EditableProfileField";
import { UserComments } from "./UserComments";

interface ProfileDetailsProps {
  user: UserRecord | null;
}

export const UserProfile: React.FC<ProfileDetailsProps> = ({ user }) => {
  const theme = useTheme();

  // Grab currentUser to determine if user should be able to edit profile fields
  const { currentUser } = useAuth();

  if (!user) {
    return <></>;
  }

  return (
    <Box
      sx={{
        mt: theme.spacing(2),
        mb: theme.spacing(2),
        gap: theme.spacing(3),
        ml: theme.spacing(2),
      }}
    >
      <Avatar
        sx={{ width: 100, height: 100, mb: theme.spacing(1) }}
        src={user?.picture_url}
        imgProps={{ referrerPolicy: "no-referrer" }}
      />
      <Box
        sx={{
          display: "flex",
          gap: theme.spacing(3),
        }}
      >
        <Box
          sx={{
            width: "80%",
          }}
        >
          <List>
            <EditableProfileField
              title="Name"
              field="name"
              value={user?.name}
              maxLength={70}
              editable={
                currentUser?.is_system_user ||
                currentUser?.user_id === user.user_id
              }
              userId={user.user_id}
            />
            <ListItem
              sx={{
                pl: 0,
              }}
            >
              <ListItemText
                primary="Email"
                secondary={user?.user_id}
                secondaryTypographyProps={{
                  mt: theme.spacing(0.5),
                }}
              />
            </ListItem>
            <EditableProfileField
              title="Picture"
              field="picture_url"
              value={user?.picture_url}
              editable={
                currentUser?.is_system_user ||
                currentUser?.user_id === user.user_id
              }
              userId={user.user_id}
            />
          </List>
        </Box>
      </Box>
      <UserComments userId={user.user_id} />
    </Box>
  );
};
